// Pure data helpers of the automations panel and the calendar: API list shapes, access-filtered
// agent choices grouped by floor and department, routine/trigger grouping and search, form ↔ request
// body mapping and form validation. DOM-free.
import { can, sortedFloors, floorDepartments, deptAgents } from '../../store.js';
import { builderToSchedule, validateSchedule } from './schedule.js';
import { isHttpUrl } from './template.js';

export const ROUTINE_TITLE_MAX = 90;
export const ROUTINE_PROMPT_MAX = 8000;
export const TRIGGER_NAME_MAX = 90;
export const TRIGGER_TEMPLATE_MAX = 8000;
export const JSON_EXAMPLE_MAX = 4000;
/** Bounds of a callback secret (server/triggers/service.js). */
export const CALLBACK_SECRET_MIN = 8;
export const CALLBACK_SECRET_MAX = 256;
export const AUTONOMIES = ['inherit', 'approve', 'auto'];
export const OUTPUT_FORMATS = ['text', 'json'];
export const ENGINES = ['claude', 'codex', 'openai-image', 'gemini', 'kie'];
/** Engines that deserve a one-line explanation when chosen (they have no tools). */
const ENGINE_HINTS = Object.freeze({ gemini: 'auto.engineHint.gemini', 'openai-image': 'auto.engineHint.openai-image', kie: 'auto.engineHint.kie' });
export const MODELS = ['sonnet', 'opus', 'haiku', 'fable'];
export const EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'];

/**
 * Accepts `[...]` or `{ <key>: [...] }` (the SPEC does not fix the envelope of list endpoints).
 * @param {unknown} res
 * @param {string} key
 * @returns {Array<object>}
 */
/**
 * The string key explaining an engine choice in the routine editor (pure): Gemini analyses videos
 * and images without tools, Images OpenAI makes one picture; null for the others and for "default".
 * @param {string} engine
 * @returns {string|null}
 */
export function engineHintKey(engine) {
  return Object.prototype.hasOwnProperty.call(ENGINE_HINTS, engine) ? ENGINE_HINTS[engine] : null;
}

export function toList(res, key) {
  if (Array.isArray(res)) return res.filter((x) => x && typeof x === 'object');
  if (res && Array.isArray(res[key])) return res[key].filter((x) => x && typeof x === 'object');
  return [];
}

/** ms / ISO / Date → ms (null when missing or invalid). */
export function toMs(v) {
  if (v == null || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.getTime();
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v);
  const n = /^\d+$/.test(s) ? Number(s) : Date.parse(s);
  return Number.isFinite(n) ? n : null;
}

/** Lower case without accents, for searching. */
export function normalizeText(s) {
  return String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/** True when every word of `query` appears in one of `fields`. */
export function matchesQuery(fields, query) {
  const q = normalizeText(query);
  if (!q) return true;
  const hay = normalizeText(fields.filter(Boolean).join(' '));
  return q.split(/\s+/).every((w) => hay.includes(w));
}

/** Replaces (or appends) the item with the same id; returns a new array. */
export function upsertById(list, item) {
  if (!item || !item.id) return list;
  const i = list.findIndex((x) => x.id === item.id);
  if (i < 0) return [...list, item];
  const next = [...list];
  next[i] = { ...list[i], ...item };
  return next;
}

/** Removes the item with this id; returns a new array. */
export function removeById(list, id) {
  return list.filter((x) => x.id !== id);
}

/**
 * Agents the user may bind to a routine or trigger (department level ≥ `need`), grouped by floor
 * then department, for an <optgroup> select.
 * @param {object} state app store state
 * @param {'view'|'operate'|'manage'} [need]
 * @returns {Array<{floorId: string, deptId: string, label: string, color: string, agents: object[]}>}
 */
export function agentGroups(state, need = 'manage') {
  const { floors = [], departments = [], agents = [] } = state.office || {};
  const groups = [];
  for (const f of sortedFloors(floors)) {
    for (const d of floorDepartments(departments, f.id)) {
      if (!can(state, need, { deptId: d.id })) continue;
      const list = deptAgents(agents, d.id);
      if (!list.length) continue;
      groups.push({ floorId: f.id, deptId: d.id, label: `${f.name} · ${d.name}`, color: d.color, agents: list });
    }
  }
  return groups;
}

/** Office lookups by id. */
export function officeIndex(office = {}) {
  const floors = new Map((office.floors || []).map((f) => [f.id, f]));
  const depts = new Map((office.departments || []).map((d) => [d.id, d]));
  const agents = new Map((office.agents || []).map((a) => [a.id, a]));
  return { floors, depts, agents };
}

/**
 * Groups items (routines or triggers) by floor then department, in building order, after filtering
 * by floor and search text. Items of unknown departments go last under "Autres".
 * @param {object[]} items each with floorId, deptId, agentId
 * @param {object} office
 * @param {{floorId?: string, agentId?: string, query?: string, text?: (item) => string[]}} [opts]
 * @returns {Array<{key: string, floorId: string, deptId: string, floorName: string, deptName: string, color: string, items: object[]}>}
 */
export function groupByDept(items, office, { floorId = '', agentId = '', query = '', text = () => [] } = {}) {
  const idx = officeIndex(office);
  const order = deptOrder(office);
  const groups = new Map();
  for (const it of items) {
    if (floorId && it.floorId !== floorId) continue;
    if (agentId && it.agentId !== agentId) continue;
    const agent = idx.agents.get(it.agentId);
    const dept = idx.depts.get(it.deptId);
    const floor = idx.floors.get(it.floorId);
    if (!matchesQuery([...text(it), agent?.name, agent?.role, dept?.name, floor?.name], query)) continue;
    const key = `${it.floorId || ''}/${it.deptId || ''}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key, floorId: it.floorId, deptId: it.deptId, floorName: floor?.name || '', deptName: dept?.name || '',
        color: dept?.color || '', rank: order.has(it.deptId) ? order.get(it.deptId) : Number.MAX_SAFE_INTEGER, items: [],
      });
    }
    groups.get(key).items.push(it);
  }
  return [...groups.values()].sort((a, b) => a.rank - b.rank || a.key.localeCompare(b.key));
}

/** Department id → rank in building order (floors by order, then their departments). */
function deptOrder(office) {
  const order = new Map();
  let i = 0;
  for (const f of sortedFloors(office?.floors || [])) {
    for (const d of floorDepartments(office?.departments || [], f.id)) order.set(d.id, i++);
  }
  return order;
}

/**
 * Groups items by agent (« Par agent »), in building order then lead first and by name, after the
 * same filters as groupByDept. Items of unknown agents go last under their id.
 * @param {object[]} items each with floorId, deptId, agentId
 * @param {object} office
 * @param {{floorId?: string, agentId?: string, query?: string, text?: (item) => string[]}} [opts]
 * @returns {Array<{key: string, agentId: string, agentName: string, agentRole: string, floorId: string, deptId: string,
 *   floorName: string, deptName: string, color: string, items: object[]}>}
 */
export function groupByAgent(items, office, { floorId = '', agentId = '', query = '', text = () => [] } = {}) {
  const idx = officeIndex(office);
  const order = deptOrder(office);
  const groups = new Map();
  for (const it of items) {
    if (floorId && it.floorId !== floorId) continue;
    if (agentId && it.agentId !== agentId) continue;
    const agent = idx.agents.get(it.agentId);
    const dept = idx.depts.get(agent?.deptId || it.deptId);
    const floor = idx.floors.get(agent?.floorId || it.floorId);
    if (!matchesQuery([...text(it), agent?.name, agent?.role, dept?.name, floor?.name], query)) continue;
    const key = String(it.agentId || '');
    if (!groups.has(key)) {
      groups.set(key, {
        key, agentId: key, agentName: agent?.name || '', agentRole: agent?.role || '', lead: !!agent?.lead,
        floorId: floor?.id || it.floorId, deptId: dept?.id || it.deptId, floorName: floor?.name || '', deptName: dept?.name || '',
        color: dept?.color || '', rank: agent && order.has(dept?.id) ? order.get(dept.id) : Number.MAX_SAFE_INTEGER, items: [],
      });
    }
    groups.get(key).items.push(it);
  }
  return [...groups.values()].sort((a, b) => a.rank - b.rank || Number(b.lead) - Number(a.lead)
    || (a.agentName || a.key).localeCompare(b.agentName || b.key, 'fr'));
}

/**
 * The agents that have at least one of the items, for the « Agent » filter: [{id, name, label}]
 * in building order (label "NAME — Étage · Service").
 * @param {object[]} items
 * @param {object} office
 */
export function agentsWithItems(items, office) {
  const ids = new Set(items.map((it) => it.agentId).filter(Boolean));
  const idx = officeIndex(office);
  const order = deptOrder(office);
  return [...ids].map((id) => idx.agents.get(id)).filter(Boolean)
    .sort((a, b) => (order.get(a.deptId) ?? Infinity) - (order.get(b.deptId) ?? Infinity)
      || Number(!!b.lead) - Number(!!a.lead) || String(a.name).localeCompare(String(b.name), 'fr'))
    .map((a) => ({
      id: a.id, name: a.name,
      label: [a.name, [idx.floors.get(a.floorId)?.name, idx.depts.get(a.deptId)?.name].filter(Boolean).join(' · ')].filter(Boolean).join(' — '),
    }));
}

/** Routines sorted: active first by next run, then paused by title. */
export function sortRoutines(list) {
  return [...list].sort((a, b) => {
    if (!!a.paused !== !!b.paused) return a.paused ? 1 : -1;
    const na = toMs(a.state?.nextAt ?? a.nextAt) ?? Infinity;
    const nb = toMs(b.state?.nextAt ?? b.nextAt) ?? Infinity;
    if (na !== nb) return na - nb;
    return String(a.title || '').localeCompare(String(b.title || ''), 'fr');
  });
}

/** Triggers sorted: enabled first, then by name. */
export function sortTriggers(list) {
  return [...list].sort((a, b) => {
    if (!!a.enabled !== !!b.enabled) return a.enabled ? -1 : 1;
    return String(a.name || a.id).localeCompare(String(b.name || b.id), 'fr');
  });
}

/** Next run of a routine (ms or null), whatever the server's field name. */
export function routineNextAt(r) {
  return toMs(r?.state?.nextAt ?? r?.nextAt);
}

/** Last run of a routine (ms or null). */
export function routineLastAt(r) {
  return toMs(r?.state?.lastAt ?? r?.lastAt);
}

/**
 * When the last run of a routine found nothing to do (RIEN_A_FAIRE, SPEC-EQUIPE §3): ms, or null
 * when the last run did some work (or never noted an idle pass).
 */
export function routineLastIdleAt(r) {
  const idle = toMs(r?.state?.lastIdleAt);
  if (!idle) return null;
  const last = routineLastAt(r);
  return !last || idle >= last ? idle : null;
}

/**
 * When the last run of a routine was blocked by an external cause (« BLOQUÉ : … », SPEC-EQUIPE §3):
 * {at, reason}, or null when a later run started (or it never was).
 * @returns {{at: number, reason: string}|null}
 */
export function routineLastBlocked(r) {
  const at = toMs(r?.state?.lastBlockedAt);
  if (!at) return null;
  const last = routineLastAt(r);
  if (last && at < last) return null;
  const reason = typeof r.state.lastBlockedReason === 'string' ? r.state.lastBlockedReason.trim() : '';
  return { at, reason };
}

/**
 * When the last scheduled pass of a routine was skipped because its previous task was still
 * unfinished (queued, running, awaiting approval): ms, or null when a run started since.
 */
export function routineLastSkippedAt(r) {
  const at = toMs(r?.state?.lastSkippedAt);
  if (!at) return null;
  const last = routineLastAt(r);
  return !last || at >= last ? at : null;
}

/** Runs count of a routine. */
export function routineRuns(r) {
  const n = Number(r?.state?.runs ?? r?.runs);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// ---------------------------------------------------------------------------------------------
// Routine form

/**
 * The routine editor's form state from a routine (or defaults for a new one).
 * @param {object|null} routine
 * @param {{agentId?: string, builder?: object}} [defaults]
 */
export function routineForm(routine, defaults = {}) {
  const r = routine || {};
  return {
    agentId: r.agentId || defaults.agentId || '',
    title: r.title || defaults.title || '',
    prompt: r.prompt || defaults.prompt || '',
    autonomy: AUTONOMIES.includes(r.autonomy) ? r.autonomy : (defaults.autonomy || 'inherit'),
    outputFormat: OUTPUT_FORMATS.includes(r.outputFormat) ? r.outputFormat : 'text',
    paused: routine ? !!r.paused : !!defaults.paused,
    engine: r.engine || '',
    model: r.model || '',
    effort: r.effort || '',
  };
}

/**
 * Checks the routine form; returns field → French message (empty object when valid).
 * @param {object} form routineForm() + builder
 * @param {object} builder schedule builder
 * @param {object} state app state (agent lookup)
 */
export function validateRoutineForm(form, builder, state) {
  const errors = {};
  const agent = (state?.office?.agents || []).find((a) => a.id === form.agentId);
  if (!agent) errors.agentId = 'Choisissez l’agent qui fera la routine.';
  if (!String(form.title || '').trim()) errors.title = 'Donnez un titre à la routine.';
  else if (form.title.trim().length > ROUTINE_TITLE_MAX) errors.title = `${ROUTINE_TITLE_MAX} caractères au plus.`;
  if (!String(form.prompt || '').trim()) errors.prompt = 'Écrivez ce que l’agent doit faire à chaque passage.';
  else if (form.prompt.length > ROUTINE_PROMPT_MAX) errors.prompt = `${ROUTINE_PROMPT_MAX} caractères au plus.`;
  const problems = validateSchedule(builderToSchedule(builder));
  if (problems.length) errors.schedule = problems[0];
  return errors;
}

/**
 * Body of POST /api/routines and PATCH /api/routines/:id. engine/model/effort are sent when chosen, or as
 * '' to clear a value the routine had (they stay out otherwise: the agent's own apply).
 * @param {object} form
 * @param {object} builder
 * @param {object} state app state (the agent gives floorId/deptId)
 * @param {object|null} [previous] the routine being edited
 */
export function routineBody(form, builder, state, previous = null) {
  const agent = (state?.office?.agents || []).find((a) => a.id === form.agentId) || {};
  const body = {
    floorId: agent.floorId, deptId: agent.deptId, agentId: form.agentId,
    title: String(form.title || '').trim(), prompt: String(form.prompt || '').trim(),
    schedule: builderToSchedule(builder), autonomy: form.autonomy, outputFormat: form.outputFormat,
    paused: !!form.paused,
  };
  for (const key of ['engine', 'model', 'effort']) {
    const v = String(form[key] || '').trim();
    if (v) body[key] = v;
    else if (previous && previous[key]) body[key] = '';
  }
  return body;
}

/** First line of a text, cut to `n` characters (routine titles from free text). */
export function firstLineOf(text, n = ROUTINE_TITLE_MAX) {
  const line = String(text ?? '').split('\n').map((l) => l.trim()).find(Boolean) || '';
  return line.length > n ? `${line.slice(0, n - 1).trimEnd()}…` : line;
}

// ---------------------------------------------------------------------------------------------
// Trigger form

/**
 * The trigger editor's form state from a trigger (or defaults for a new one).
 * @param {object|null} trigger
 */
export function triggerForm(trigger, defaults = {}) {
  const tr = trigger || {};
  const cb = tr.callback && typeof tr.callback === 'object' ? tr.callback : null;
  return {
    name: tr.name || '',
    agentId: tr.agentId || defaults.agentId || '',
    enabled: trigger ? !!tr.enabled : true,
    promptTemplate: tr.promptTemplate || '',
    autonomy: AUTONOMIES.includes(tr.autonomy) ? tr.autonomy : 'inherit',
    outputFormat: OUTPUT_FORMATS.includes(tr.outputFormat) ? tr.outputFormat : 'text',
    jsonExample: typeof tr.jsonExample === 'string' ? tr.jsonExample : '',
    callbackUrl: cb?.url || '',
    callbackSecret: '',
    hasCallbackSecret: !!(tr.hasCallbackSecret || (cb && (cb.hasSecret || cb.secret || cb.secretSet))),
    clearCallbackSecret: false,
  };
}

/**
 * Checks the trigger form; returns field → French message.
 * @param {object} form
 * @param {object} state
 */
export function validateTriggerForm(form, state) {
  const errors = {};
  if (!String(form.name || '').trim()) errors.name = 'Donnez un nom au déclencheur.';
  else if (form.name.trim().length > TRIGGER_NAME_MAX) errors.name = `${TRIGGER_NAME_MAX} caractères au plus.`;
  const agent = (state?.office?.agents || []).find((a) => a.id === form.agentId);
  if (!agent) errors.agentId = 'Choisissez l’agent qui recevra les appels.';
  if (!String(form.promptTemplate || '').trim()) errors.promptTemplate = 'Écrivez la consigne donnée à l’agent à chaque appel.';
  else if (form.promptTemplate.length > TRIGGER_TEMPLATE_MAX) errors.promptTemplate = `${TRIGGER_TEMPLATE_MAX} caractères au plus.`;
  if (form.outputFormat === 'json' && form.jsonExample.trim()) {
    if (form.jsonExample.length > JSON_EXAMPLE_MAX) errors.jsonExample = `${JSON_EXAMPLE_MAX} caractères au plus.`;
    else {
      try { JSON.parse(form.jsonExample); } catch { errors.jsonExample = 'L’exemple doit être du JSON valide.'; }
    }
  }
  const url = String(form.callbackUrl || '').trim();
  if (url && !isHttpUrl(url)) errors.callbackUrl = 'Adresse invalide : elle doit commencer par http:// ou https://.';
  const cbSecret = String(form.callbackSecret || '');
  if (!url && cbSecret.trim()) errors.callbackSecret = 'Un secret de rappel n’a de sens qu’avec une adresse de rappel.';
  else if (cbSecret && (cbSecret.length < CALLBACK_SECRET_MIN || cbSecret.length > CALLBACK_SECRET_MAX || /\s/.test(cbSecret))) {
    errors.callbackSecret = `Secret de rappel invalide : ${CALLBACK_SECRET_MIN} à ${CALLBACK_SECRET_MAX} caractères, sans espace.`;
  }
  return errors;
}

/**
 * Body of POST /api/triggers and PATCH /api/triggers/:id. The callback secret is only sent when
 * typed (editing leaves it unchanged), or as '' when the user asked to remove it.
 * @param {object} form
 * @param {object} state
 * @param {{editing?: boolean}} [opts]
 */
export function triggerBody(form, state, { editing = false } = {}) {
  const agent = (state?.office?.agents || []).find((a) => a.id === form.agentId) || {};
  const body = {
    floorId: agent.floorId, deptId: agent.deptId, agentId: form.agentId,
    name: String(form.name || '').trim(), enabled: !!form.enabled,
    promptTemplate: String(form.promptTemplate || ''), autonomy: form.autonomy, outputFormat: form.outputFormat,
    jsonExample: form.outputFormat === 'json' ? String(form.jsonExample || '').trim() : '',
  };
  const url = String(form.callbackUrl || '').trim();
  if (!url) body.callback = null;
  else {
    body.callback = { url };
    const secret = String(form.callbackSecret || '');
    if (secret.trim()) body.callback.secret = secret;
    else if (editing && form.clearCallbackSecret) body.callback.secret = '';
    else if (!editing) { /* no secret: unsigned callbacks */ }
  }
  return body;
}

/**
 * Where a failed save should show the server's message: the callback URL field when the error is
 * about the callback / allowlist, else the form.
 * @param {{message?: string, details?: any}} err ApiError
 * @returns {'callbackUrl'|'callbackSecret'|'name'|'agentId'|'promptTemplate'|'form'}
 */
export function triggerErrorField(err) {
  const d = err?.details;
  const field = typeof d?.field === 'string' ? d.field : '';
  if (/callback\.?secret/i.test(field)) return 'callbackSecret';
  if (/callback/i.test(field)) return 'callbackUrl';
  if (['name', 'agentId', 'promptTemplate'].includes(field)) return field;
  const msg = normalizeText(err?.message);
  if (/secret de rappel|callback secret/.test(msg)) return 'callbackSecret';
  if (/allowlist|rappel|callback|autorisee|autorisees/.test(msg)) return 'callbackUrl';
  return 'form';
}

/** Id of the task created by POST /api/triggers/:id/test or /api/routines/:id/run. */
export function createdTaskId(res) {
  if (!res || typeof res !== 'object') return null;
  return res.taskId || res.task?.id || res.id || null;
}
